import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Sparkles, Coins, RefreshCw, ShoppingCart, ShieldCheck, AlertTriangle,
  TrendingUp, History, ArrowUpRight, Loader2, Crown, Infinity as InfinityIcon,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import AnnualUpgradeBanner from "@/components/AnnualUpgradeBanner";
import ReferralCard from "@/components/ReferralCard";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { CurrencyPicker } from "@/components/CurrencyPicker";

const FEATURE_LABEL = {
  generate_bio: "Artist bio",
  generate_caption: "Social caption",
  generate_social_post: "Social media post",
  generate_hashtags: "Hashtags",
  generate_dj_script: "AI DJ script",
  generate_news_bulletin: "News bulletin",
  generate_podcast: "Podcast meta",
  talk_show_to_podcast: "Talk show → podcast notes",
  generate_campaign_pack: "Full social campaign",
  translate_content: "Translation",
  generate_commercial: "Commercial script",
  imaging_generate: "Imaging / image gen",
  transcribe: "Transcription",
  ai_default: "AI generation",
  stream_bio: "Streaming bio",
  generate_cover: "Cover art",
  blog_post: "Blog post",
  topup: "Credit top-up purchased",
  admin_grant: "Admin grant",
  admin_revoke: "Admin revoke",
  renewal_reset: "Monthly renewal",
  translation: "Translation",
  welcome_bonus: "Welcome bonus",
  referral_bonus: "Referral bonus",
};

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return d; }
}
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

export default function BillingCredits({ embedded = false }) {
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [costs, setCosts] = useState({});
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupBusy, setTopupBusy] = useState(null);
  const [payVia, setPayVia] = useState("stripe");
  const { currency, setCurrency, fmt: fx, isUsd } = useCurrency();
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const [b, h, c, p] = await Promise.all([
        api.get("/credits/balance"),
        api.get("/credits/history?limit=100"),
        api.get("/credits/costs"),
        api.get("/credits/topup-packs"),
      ]);
      setBalance(b.data);
      setHistory(h.data.items || []);
      setCosts(c.data.costs || {});
      setPacks(p.data.packs || []);
    } catch (e) {
      toast.error("Could not load billing data");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const topupResult = params.get("topup");
    if (topupResult === "success") {
      toast.success("Top-up complete! Credits are on your way.");
      setTimeout(load, 2000);
      setParams({}, { replace: true });
    } else if (topupResult === "cancelled") {
      toast.message("Top-up cancelled.");
      setParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buyPack = async (sku) => {
    setTopupBusy(sku);
    try {
      const r = await api.post("/credits/topup/checkout", { sku, provider: payVia });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("The payment provider didn't return a checkout URL");
        setTopupBusy(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setTopupBusy(null);
    }
  };

  if (loading || !balance) {
    return <div className="p-10 font-mono text-xs text-ink-muted">LOADING BILLING...</div>;
  }

  const isAdmin = balance.admin_unlimited;
  const pct = isAdmin || !balance.monthly_allowance
    ? 100
    : Math.max(0, Math.min(100, Math.round(100 * (balance.monthly_remaining / balance.monthly_allowance))));
  const showLow = !isAdmin && balance.low_credit;

  return (
    <div className={embedded ? "animate-fade-up" : "p-6 md:p-10 max-w-7xl mx-auto animate-fade-up"} data-testid="billing-credits-page">
      {!embedded && (
        <PageHeader
          eyebrow="// BILLING & CREDITS"
          title="Your credits, your billing, all in one place."
          description="Track monthly usage, top up anytime, and review every AI deduction."
        />
      )}

      {/* Low credit banner */}
      {showLow && (
        <div
          className="mb-6 rounded-xl border border-brand-amber/40 bg-brand-amber/5 p-4 flex items-center gap-3"
          data-testid="low-credit-warning"
        >
          <AlertTriangle className="h-5 w-5 text-brand-amber shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-semibold text-brand-amber">Heads up — you're under 10% of your monthly allowance.</span>
            <span className="text-ink-secondary ml-2">Top up below to keep generating.</span>
          </div>
        </div>
      )}

      {/* Welcome bonus banner (shows once when the user still has unspent bonus) */}
      {!isAdmin && history.some((h) => h.kind === "welcome_bonus") && balance.topup_balance > 0 && (
        <div
          className="mb-6 rounded-xl border border-brand-cyan/40 bg-brand-cyan/5 p-4 flex items-center gap-3"
          data-testid="welcome-bonus-banner"
        >
          <Sparkles className="h-5 w-5 text-brand-cyan shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-semibold text-brand-cyan">Welcome aboard! Onboarding credits applied.</span>
            <span className="text-ink-secondary ml-2">
              Try a 10-credit campaign pack or 5-credit talk-show notes on us.
            </span>
          </div>
        </div>
      )}

      {/* Top row: plan card + balance card */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="current-plan-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-mono uppercase tracking-wider text-ink-muted">Current plan</div>
            {isAdmin && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider rounded-full bg-violet-500/15 text-violet-300 px-2 py-0.5">
                <Crown className="h-3 w-3" /> Admin
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5 text-brand-amber" />
            <div className="font-display text-3xl font-bold capitalize">
              {String(balance.plan || "free").replace("_", " ")}
            </div>
          </div>
          <div className="text-xs text-ink-secondary mb-4">
            Resets on <span className="font-mono text-ink-primary">{fmtDate(balance.period_end)}</span>
          </div>
          <a
            href="/dashboard/billing?tab=plan"
            className="inline-flex items-center gap-1 text-xs rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-3 py-1.5 hover:bg-brand-amber/20 transition"
            data-testid="upgrade-plan-btn"
          >
            <TrendingUp className="h-3.5 w-3.5" /> Upgrade plan <ArrowUpRight className="h-3 w-3" />
          </a>
          <div>
            <AnnualUpgradeBanner compact />
          </div>
        </div>

        <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="balance-card">
          <div className="text-xs font-mono uppercase tracking-wider text-ink-muted mb-3">Credits</div>
          {isAdmin ? (
            <div className="flex items-center gap-2 mb-3">
              <InfinityIcon className="h-7 w-7 text-violet-300" />
              <div className="font-display text-3xl font-bold">Unlimited</div>
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-2 mb-1">
                <div className="font-display text-4xl font-bold text-brand-amber">
                  {balance.total_remaining.toLocaleString()}
                </div>
                <div className="text-sm text-ink-muted">remaining</div>
              </div>
              <div className="text-xs text-ink-secondary mb-3">
                Monthly: <span className="font-mono text-ink-primary">
                  {balance.monthly_remaining.toLocaleString()} / {balance.monthly_allowance.toLocaleString()}
                </span>
                {balance.topup_balance > 0 && (
                  <span className="ml-2">+ Top-up: <span className="font-mono text-ink-primary">{balance.topup_balance.toLocaleString()}</span></span>
                )}
              </div>
              {/* Usage bar */}
              <div className="h-2 bg-bg-elevated rounded-full overflow-hidden mb-2" aria-label="monthly usage">
                <div
                  className={`h-2 ${pct < 10 ? "bg-rose-500" : pct < 25 ? "bg-brand-amber" : "bg-emerald-500"}`}
                  style={{ width: `${pct}%` }}
                  data-testid="credit-usage-bar"
                />
              </div>
              <div className="text-[11px] font-mono text-ink-muted">
                Used this period: <span className="text-ink-primary">{balance.monthly_used.toLocaleString()}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Referral card */}
      <div className="mb-6">
        <ReferralCard />
      </div>

      {/* Top-up packs */}
      <div className="rounded-xl border border-line bg-bg-surface p-6 mb-6" data-testid="topup-packs">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-brand-amber" />
            <h3 className="font-display text-xl font-bold">Buy more credits</h3>
          </div>
          <CurrencyPicker currency={currency} onChange={setCurrency} />
        </div>
        <div className="text-xs text-ink-secondary mb-3">
          Top-up credits never expire — they're added on top of your monthly allowance.
          {!isUsd && <span className="text-ink-muted"> Prices shown in {currency} — checkout is billed in USD.</span>}
        </div>
        <div className="flex items-center gap-2 mb-4" data-testid="topup-provider-toggle">
          <span className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Pay with</span>
          {["stripe", "creem"].map((pv) => (
            <button
              key={pv}
              onClick={() => setPayVia(pv)}
              data-testid={`topup-provider-${pv}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                payVia === pv
                  ? "border-brand-amber bg-brand-amber/10 text-brand-amber"
                  : "border-line text-ink-secondary hover:border-ink-muted"
              }`}
            >
              {pv === "stripe" ? "Stripe" : "Creem"}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {packs.map((p) => (
            <button
              key={p.sku}
              onClick={() => buyPack(p.sku)}
              disabled={topupBusy === p.sku || isAdmin}
              data-testid={`topup-pack-${p.sku}`}
              className={`relative rounded-xl border bg-bg-elevated p-4 text-left transition hover:border-brand-amber disabled:opacity-50 disabled:cursor-not-allowed ${
                p.sku === "pack_2500" ? "border-brand-amber" : "border-line"
              }`}
            >
              {p.sku === "pack_2500" && (
                <span className="absolute -top-2 right-3 rounded-full bg-brand-amber text-bg text-[9px] font-bold px-2 py-0.5 tracking-wider">
                  BEST VALUE
                </span>
              )}
              <div className="flex items-center gap-1 mb-2">
                <Coins className="h-4 w-4 text-brand-amber" />
                <span className="font-display text-xl font-bold">{p.credits.toLocaleString()}</span>
                <span className="text-xs text-ink-muted">credits</span>
              </div>
              <div className="text-2xl font-bold text-brand-cyan">{fx(p.price_usd)}</div>
              <div className="text-[11px] font-mono text-ink-muted mt-1">
                {fx(p.price_usd / p.credits * 1000, { decimals: 2 })}/1k credits
              </div>
              {topupBusy === p.sku && (
                <Loader2 className="h-4 w-4 animate-spin absolute top-3 right-3 text-brand-amber" />
              )}
            </button>
          ))}
        </div>
        {isAdmin && (
          <div className="mt-3 text-[11px] text-ink-muted italic">
            Admin accounts have unlimited credits — top-up disabled.
          </div>
        )}
      </div>

      {/* Cost table */}
      <details className="mb-6 rounded-xl border border-line bg-bg-surface" data-testid="costs-table">
        <summary className="cursor-pointer p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand-cyan" />
            <span className="font-display font-bold">Credit costs per feature</span>
          </div>
          <span className="text-xs text-ink-muted">expand</span>
        </summary>
        <div className="px-4 pb-4">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {Object.entries(costs)
              .filter(([k]) => !["ai_default", "ai_compose", "ai_ideate", "stream_bio"].includes(k))
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-1 border-b border-line/30" data-testid={`cost-row-${k}`}>
                <span className="text-ink-secondary">{FEATURE_LABEL[k] || k}</span>
                <span className="font-mono text-brand-amber">{v} {v === 1 ? "credit" : "credits"}</span>
              </div>
            ))}
          </div>
        </div>
      </details>

      {/* History */}
      <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="usage-history">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-brand-cyan" />
            <h3 className="font-display text-xl font-bold">Usage history</h3>
          </div>
          <button onClick={load} className="text-xs text-ink-muted hover:text-brand-cyan inline-flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        {history.length === 0 ? (
          <div className="text-center py-10 text-sm text-ink-muted">No activity yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs font-mono uppercase tracking-wider text-ink-muted">
                <tr className="border-b border-line">
                  <th className="py-2">Date</th>
                  <th>Feature</th>
                  <th>Type</th>
                  <th className="text-right">Δ Credits</th>
                  <th className="text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => {
                  const isSpend = (h.credits || 0) < 0;
                  return (
                    <tr key={i} className="border-b border-line/30" data-testid={`history-row-${i}`}>
                      <td className="py-2 text-xs font-mono text-ink-muted whitespace-nowrap">{fmt(h.created_at)}</td>
                      <td className="text-ink-primary">{FEATURE_LABEL[h.feature] || h.feature}</td>
                      <td>
                        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${
                          h.kind === "spend" ? "bg-bg-elevated text-ink-muted" :
                          h.kind === "topup" ? "bg-brand-amber/15 text-brand-amber" :
                          h.kind === "renewal" ? "bg-brand-cyan/15 text-brand-cyan" :
                          h.kind === "welcome_bonus" ? "bg-brand-cyan/15 text-brand-cyan" :
                          h.kind === "referral_bonus" ? "bg-brand-amber/15 text-brand-amber" :
                          h.kind === "admin_grant" ? "bg-violet-500/15 text-violet-300" :
                          h.kind === "admin_revoke" ? "bg-rose-500/15 text-rose-300" :
                          "bg-bg-elevated text-ink-muted"
                        }`}>{h.kind || "spend"}</span>
                      </td>
                      <td className={`text-right font-mono ${isSpend ? "text-rose-400" : "text-emerald-400"}`}>
                        {isSpend ? "" : "+"}{h.credits}
                      </td>
                      <td className="text-right font-mono text-ink-primary">{(h.balance_after ?? "—").toLocaleString?.() ?? h.balance_after}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
